import React, { useCallback, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import siteData from '../data/site.json';

const navItems = [
  { to: '/work', label: 'Work' },
  { to: '/labs', label: 'Labs' },
  { to: '/about', label: 'About' },
  // { to: '/blog', label: 'Blog' },
  { to: '/contact', label: 'Contact' },
];

const mobileItems = [
  { to: '/', label: 'Home' },
  { to: '/work', label: 'Work' },
  { to: '/labs', label: 'Labs' },
  { to: '/about', label: 'About' },
  // { to: '/blog', label: 'Blog' },
  { to: '/contact', label: 'Contact Me', isPrimary: true },
];

const getNavClass = ({ isActive }) =>
  `navbar-menu-item w-inline-block${isActive ? ' w--current' : ''}`;

const getMobileClass = ({ isActive, isPrimary }) => {
  const activeClass = isActive ? ' w--current' : '';
  const primaryClass = isPrimary ? ' primary' : '';
  return `mobile-menu-nav-item w-inline-block${primaryClass}${activeClass}`;
};

const getFooterNavClass = ({ isActive }) =>
  `nav-item inverse w-inline-block${isActive ? ' w--current' : ''}`;

const handleKeyActivate = (handler) => (event) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    handler();
  }
};

const Nav = () => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const openMobile = useCallback(() => setIsMobileOpen(true), []);
  const closeMobile = useCallback(() => setIsMobileOpen(false), []);

  return (
    <>
      <div className="header">
        <Link to="/" className="logo w-inline-block">
          <img src="/images/Logo.svg" loading="lazy" alt="Logo Icon" />
          <div className="logo-inner">
            <div className="logo-text">joportus</div>
            <div className="logo-text-hover">jo</div>
          </div>
        </Link>
        <div className="corner-top-left top w-embed">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 0L0 20C0 8.95431 8.95431 0 20 0L0 0Z" fill="white"></path>
          </svg>
        </div>
        <div className="corner-top-left bottom w-embed">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 0L0 20C0 8.95431 8.95431 0 20 0L0 0Z" fill="white"></path>
          </svg>
        </div>
        <div className="navbar-menu">
          {navItems.map((item) => (
            <NavLink key={item.to} to={item.to} className={getNavClass}>
              <div className="navbar-menu-item-inner">
                <div className="navbar-menu-item-text">{item.label}</div>
                <div className="navbar-menu-item-text-hover">{item.label}</div>
              </div>
            </NavLink>
          ))}
        </div>
      </div>

      <div className={`mobile-menu${isMobileOpen ? ' is-open' : ''}`}>
        <div
          className="mobile-menu-close"
          role="button"
          tabIndex={0}
          aria-label="Close mobile menu"
          onClick={closeMobile}
          onKeyDown={handleKeyActivate(closeMobile)}
        >
          <div className="text-meta text-color-inverse">Close</div>
          <div className="corner-top-right top w-embed">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 0L0 20C0 8.95431 8.95431 0 20 0L0 0Z" fill="#0e1011"></path>
            </svg>
          </div>
          <div className="corner-top-right bottom w-embed">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 0L0 20C0 8.95431 8.95431 0 20 0L0 0Z" fill="#0e1011"></path>
            </svg>
          </div>
        </div>
        <div className="mobile-menu-inner">
          <div className="mobile-menu-inner-top">
            <img
              src="/images/headshots/JP2.1_BW.webp"
              loading="lazy"
              width="Auto"
              alt="Jo"
              className="border-radius-circle"
            />
            <div>
              <div className="heading-style-h5">{siteData.name}</div>
              <div className="text-meta text-color-muted-inverse">{siteData.role}</div>
            </div>
          </div>
          <div className="mobile-menu-nav">
            {mobileItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => getMobileClass({ isActive, isPrimary: item.isPrimary })}
                onClick={closeMobile}
              >
                <div className={item.isPrimary ? 'text-meta' : 'text-meta text-color-inverse'}>
                  {item.label}
                </div>
                <div className="icon-1x1-small">
                  <div className="social-card-icon w-embed">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 256 256">
                      <path d="M221.66,133.66l-72,72a8,8,0,0,1-11.32-11.32L196.69,136H40a8,8,0,0,1,0-16H196.69L138.34,61.66a8,8,0,0,1,11.32-11.32l72,72A8,8,0,0,1,221.66,133.66Z"></path>
                    </svg>
                  </div>
                </div>
              </NavLink>
            ))}
          </div>
        </div>
      </div>

      <div
        className="mobile-menu-open"
        role="button"
        tabIndex={0}
        aria-expanded={isMobileOpen}
        aria-label="Open mobile menu"
        onClick={openMobile}
        onKeyDown={handleKeyActivate(openMobile)}
      >
        <div className="text-meta">Menu</div>
        <div className="corner-top-right top w-embed">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 0L0 20C0 8.95431 8.95431 0 20 0L0 0Z" fill="white"></path>
          </svg>
        </div>
        <div className="corner-top-right bottom w-embed">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 0L0 20C0 8.95431 8.95431 0 20 0L0 0Z" fill="white"></path>
          </svg>
        </div>
      </div>
    </>
  );
};

export { getFooterNavClass };
export default Nav;

